import { AfterContentChecked, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  FormGroupDirective,
  NgForm,
  Validators
} from '@angular/forms';
import { UserDao } from '../../../../core/services/dao/userDao';
import { ActivatedRoute, Router } from '@angular/router';
import { Invite } from '../../../../core/models/invite';
import { Subject } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { AdvertiserDao } from '../../../../core/services/dao/advertiserDao';
import { TermsService } from '../../../shared/terms/terms-service';
import { PaymentDao } from '../../../../core/services/dao/paymentDao';
import { Advertiser } from '../../../../core/models/advertiser';
import { User } from '../../../../core/models/user';
import { ErrorStateMatcher } from '@angular/material/core';

export class CustomErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return control && control.invalid; // override default behavior of waiting for control to be touched to show error
  }
}

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit, AfterContentChecked {
  @Input()
  invite?: Invite;

  @Output()
  data = new EventEmitter<any>();

  public model: any = {};
  private hasShownTerms = false;
  public isBilled = false;
  public loading = false;
  public advertiser: Advertiser;
  public user: User;

  //password, email
  public toVerifyEmail: Subject<string> = new Subject<string>();
  public errors: Partial<{
    atLeastEight: boolean;
    atLeastOneCapital: boolean;
    atLeastOneLower: boolean;
    atLeastOneNumberOrSymbol: boolean;
  }> = {};
  public showPass = false;
  public oneNumber = new RegExp('.*([0-9]|[!@#$%^&*]).*');
  public oneCapital = new RegExp('(.*[A-Z].*)');
  public oneLower = new RegExp('(.*[a-z].*)');

  public matcher;

  //formGroups
  userForm: UntypedFormGroup;
  advertiserForm: UntypedFormGroup;

  constructor(
    private fb: UntypedFormBuilder,
    private userDao: UserDao,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private advertiserDao: AdvertiserDao,
    private termsService: TermsService,
    private paymentDao: PaymentDao
  ) {
    this.userForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      termsAccepted: ['', [Validators.required]]
    });

    this.advertiserForm = this.fb.group({
      name: ['', [Validators.required]]
    });

    this.matcher = new CustomErrorStateMatcher();
  }

  ngOnInit(): void {
    if (this.invite) {
      const name = this.invite.name;
      const firstName = name ? name.split(' ')[0] : '';
      const lastName = name ? (name.split(' ')[1] ? name.split(' ')[1] : '') : '';
      if (this.invite.advertisers) {
        this.advertiserForm.patchValue({ name: this.invite.advName });
        this.advertiserForm.get('name').disable();
      }
      this.userForm.patchValue({ email: this.invite.email, firstName: firstName, lastName: lastName });
    }
  }

  ngAfterContentChecked() {
    this.toVerifyEmail.next(this.userForm.get('email').value);
  }

  togglePass() {
    this.showPass = !this.showPass;
  }

  onPasswordChange(val) {
    if (val) {
      this.errors.atLeastOneCapital = this.oneCapital.test(val);
      this.errors.atLeastOneLower = this.oneLower.test(val);
      this.errors.atLeastOneNumberOrSymbol = this.oneNumber.test(val);
      this.errors.atLeastEight = val.length > 7;
    } else {
      this.errors.atLeastOneCapital = false;
      this.errors.atLeastOneLower = false;
      this.errors.atLeastOneNumberOrSymbol = false;
      this.errors.atLeastEight = false;
    }
    if (this.allKeysTrue(this.errors)) {
      this.userForm.controls.password.setErrors({});
      this.userForm.controls.password.updateValueAndValidity();
    } else {
      this.userForm.controls.password.setErrors(this.errors);
    }
  }

  allKeysTrue(obj: any): boolean {
    if (obj) {
      return Object.keys(obj).every((k) => obj[k]);
    }
    return false;
  }

  onSubmit() {
    if (this.invite) {
      this.createUserFromInvite();
    } else {
      this.createUserWithoutInvite();
    }
  }

  createUserFromInvite() {
    this.loading = true;
    const user = this.model;

    //set up model
    user.firstName = this.userForm.get('firstName').value;
    user.lastName = this.userForm.get('lastName').value;
    user.termsAccepted = this.userForm.get('termsAccepted').value;
    user.roles = this.invite.roles;
    user.email = this.userForm.get('email').value;
    user.password = this.userForm.get('password').value;
    user.bvsOnly = this.invite.bvsOnly;

    if (this.invite.agencies) {
      user.agencies = this.invite.agencies;
    }

    if (this.invite.advertisers && this.invite.advertisers.length > 0) {
      user.advertisers = this.invite.advertisers;
      user.fullAccessAdvertisers = this.invite.fullAccessAdvertisers
      user.brands = this.invite.brands;
    } else {
      user.advertisers = [];
    }

    this.userDao.createFromInvite(user, this.invite.id).subscribe(
      (createdUser) => {
        if (createdUser) {
          this.authService
            .tokenSet(this.userForm.get('email').value, this.userForm.get('password').value)
            .subscribe(() => {
              this.userDao.setCurrentUser(createdUser);
              //boot out if admin
              if (createdUser.isAdmin) {
                this.router.navigate(['manage', 'admin']).catch((err) => console.error(err));
                return;
              }
              //where we check the role given and emit to parent component
              //we also check billing here
              this.roleCheck(createdUser);
            });
        } else {
          console.error('something went wrong creating from invite');
        }
      },
      (error) => {
        console.error(error);
      }
    );
    this.loading = false;
  }

  /**
   * Note: This assumes the user will be a Point of Contact,
   * and assumes the user will be billed in app.
   */
  createUserWithoutInvite() {
    this.loading = true;
    const advertiserPost = {
      name: this.advertiserForm.get('name').value,
      pointOfContactEmail: this.userForm.get('email').value,
      pointOfContactName: this.userForm.get('firstName').value + this.userForm.get('lastName').value
    };

    this.userDao.createUser(this.userForm.value, [4]).subscribe(
      (user) => {
        if (user) {
          this.authService
            .tokenSet(this.userForm.get('email').value, this.userForm.get('password').value)
            .subscribe(() => {
              this.userDao.setCurrentUser(user);
              this.advertiserDao.create(advertiserPost).subscribe((r) => {
                const currentUser = this.userDao.getCurrentUser();
                currentUser.advertisers.push(r);
                this.userDao.updateWithPassword(currentUser, currentUser.password).subscribe((finishedUser) => {
                  //set up to send user with adv to next step
                  this.user = finishedUser;
                  // stripe set up
                  const stripePost = {
                    email: this.userForm.get('email').value,
                    name: this.advertiserForm.get('name').value,
                    metaData: {
                      advertiserId: r.id,
                      advertiser: r
                    }
                  };
                  this.paymentDao.createStripeCustomer(stripePost).subscribe(() => {});
                  //emit to parent component
                  const data = {
                    isBilled: true,
                    password: this.userForm.get('password').value,
                    user: this.user
                  };
                  this.data.emit(data);
                });
              });
            });
        } else {
          console.error('something went wrong creating user');
        }
      },
      (error) => {
        console.error(error);
      }
    );
    this.loading = false;
  }

  roleCheck(user) {
    user.roles.forEach((role) => {
      if (role.id === 2) {
        this.router.navigate(['manage']).catch((err) => console.error(err));
        return;
      }
      if (role.id === 3) {
        this.router.navigate(['manage', 'reports']).catch((err) => console.error(err));
        return;
      }
      if (role.id === 5) {
        this.router.navigate(['manage']).catch((err) => console.error(err));
        return;
      }
      if (role.id === 4) {
        this.authService.login(this.userForm.get('email').value, this.userForm.get('password').value).subscribe(() => {
          this.router.navigateByUrl('/manage/ad-codes').catch((err) => console.error(err));
          return;
        });
      }
      //If no Roles match send to manage
      if (role.id !== 4) {
        this.router.navigate(['manage', 'ad-codes']).catch((err) => console.error(err));
        return;
      }
    });
  }

  billingCheck(advertiser): boolean {
    advertiser.brands.forEach((br) => {
      if (br.isBilledInApp === true) {
        this.isBilled = true;
        return this.isBilled;
      }
    });
    return this.isBilled;
  }

  showTerms($event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.hasShownTerms = true;
    try {
      this.termsService.display(false).subscribe(
        () => {},
        (err) => console.error(err)
      );
    } catch (err) {
      console.error(err);
    }
  }

  public emailBlurred(emailString: string) {
    this.toVerifyEmail.next(emailString); //add to subject string, verification handled through [enrollEmailDuplicate] directive
  }

  public emailKeyupHandler(emailString: string) {
    this.toVerifyEmail.next(emailString); //add to subject string, verification handled through [enrollEmailDuplicate] directive
  }

  public emailFocus(emailString: string) {
    this.toVerifyEmail.next(emailString); //add to subject string, verification handled through [enrollEmailDuplicate] directive
  }
}
